import { Member } from '@sendbird/chat/groupChannel';
import { DateTime } from 'luxon';
import { isoToLocalDateTime } from './date.util';

import { CHANNEL_CLASSIFICATION, UserRoles } from '../constants/chat.constants';
import {
  NotificationEventType,
  NotificationStatus,
} from '../constants/notifications.constants';
import DOMPurify from 'dompurify';
import { createElement, ReactNode } from 'react';

export type StatusType = 'read' | 'unread';
export const READ = 'read';
export const UNREAD = 'unread';
export const statusToCategoryMapper: Record<NotificationStatus, StatusType> = {
  [NotificationStatus.READ]: READ,
  [NotificationStatus.DELIVERED]: UNREAD,
  [NotificationStatus.ARCHIVED]: READ,
  [NotificationStatus.NEW]: UNREAD,
  [NotificationStatus.UNREAD]: UNREAD,
};

export const isReadNotificationStatus = (
  status?: NotificationStatus | NotificationStatusType,
): boolean => {
  if (!status) return false;
  return statusToCategoryMapper[status as NotificationStatus] === READ;
};

export const isUnreadNotificationStatus = (
  status?: NotificationStatus | NotificationStatusType,
): boolean => {
  if (!status) return false;
  return statusToCategoryMapper[status as NotificationStatus] === UNREAD;
};

export const CHAT_EVENT_TYPES = [
  NotificationEventType.CHAT_MESSAGE,
  NotificationEventType.CHAT_REPLY,
  NotificationEventType.CHAT_MENTION,
];

const eventTypeToActionMapper: Record<
  NotificationEventType,
  (type?: string) => string
> = {
  [NotificationEventType.ADDED_TO_CARE_TEAM]: () => 'You have been added to',
  [NotificationEventType.ASSIGNED_INQUIRY]: () => 'You have been added to the',
  [NotificationEventType.CHAT_MENTION]: null,
  [NotificationEventType.CHAT_MESSAGE]: null,
  [NotificationEventType.CHAT_REPLY]: null,
  [NotificationEventType.COMPLETED_INTAKE_SURVEY]: () =>
    '<strong>Intake survey</strong> has been completed',
  [NotificationEventType.INQUIRY_STATUS_CHANGE]: () =>
    '<strong>Inquiry status</strong> has been updated',
  [NotificationEventType.INSURANCE_STATUS_CHANGE]: () =>
    '<strong>Insurance status</strong> has been updated',
  [NotificationEventType.MEDICAL_CLEARANCE_STATUS_CHANGE]: () =>
    '<strong>Medical clearance status</strong> has been updated',
  [NotificationEventType.REMOVED_FROM_CARE_TEAM]: () =>
    'You have been removed from',
  [NotificationEventType.TARGET_ADMIT_DATE_CHANGE]: () =>
    '<strong>Admit date</strong> has been updated',
  [NotificationEventType.UPDATED_ADMIN_EPISODE]: (type: string) =>
    `<strong>Admin episode ${type}</strong> has been updated`,
  [NotificationEventType.UPDATED_CONTACTS]: () =>
    '<strong>Contact</strong> has been updated',
  [NotificationEventType.UPDATED_INSURANCE_STATUS]: () =>
    '<strong>Insurance status</strong> has been updated',
  [NotificationEventType.UPDATED_MEDICAL_CLEARANCE_STATUS]: () =>
    '<strong>Medical clearance status</strong> has been updated',
  [NotificationEventType.UPLOADED_INSURANCE_CARD]: () =>
    '<strong>Insurance card</strong> has been uploaded',
};

export const isChatNotification = (
  eventType: NotificationEventType,
): boolean => {
  return CHAT_EVENT_TYPES.includes(eventType);
};

export const filterChatNotifications = (
  notifications: NotificationType[],
  isFeatureFlagEnabled: boolean,
): NotificationType[] => {
  if (!isFeatureFlagEnabled) {
    return notifications;
  }
  return notifications.filter(
    (notification) =>
      !isChatNotification(notification.eventType as NotificationEventType),
  );
};

export const calculateFilteredCounts = (
  notifications: NotificationType[],
  totalCount: number,
  totalUnreadCount: number,
  isFeatureFlagEnabled: boolean,
): { adjustedTotalCount: number; adjustedUnreadCount: number } => {
  if (!isFeatureFlagEnabled) {
    return {
      adjustedTotalCount: totalCount,
      adjustedUnreadCount: totalUnreadCount,
    };
  }

  const chatNotifications = notifications.filter((notification) =>
    isChatNotification(notification.eventType as NotificationEventType),
  );

  const chatNotificationsCount = chatNotifications.length;
  const unreadChatCount = chatNotifications.filter((notification) =>
    isUnreadNotificationStatus(notification.status),
  ).length;

  return {
    adjustedTotalCount: totalCount - chatNotificationsCount,
    adjustedUnreadCount: totalUnreadCount - unreadChatCount,
  };
};

export const updateRelatedNotifications = (
  relatedNotifications: RelatedNotificationType[],
  notificationIds: string[],
  to: NotificationStatus,
): RelatedNotificationType[] =>
  relatedNotifications.map((relatedNotification: RelatedNotificationType) =>
    notificationIds.includes(relatedNotification.id)
      ? { ...relatedNotification, status: to }
      : relatedNotification,
  );

export const updateSingleNotification = (
  notificationIds: string[],
  to: NotificationStatus,
  notifications: NotificationType[],
): NotificationType[] =>
  notifications.map((item) =>
    item.notificationExternalId === notificationIds[0]
      ? {
          ...item,
          ...(item.relatedNotifications && {
            relatedNotifications: !item.relatedNotifications
              ? []
              : updateRelatedNotifications(
                  item.relatedNotifications,
                  notificationIds,
                  to,
                ),
          }),
          status: to,
        }
      : item,
  );

export const updateMultipleNotifications = (
  notificationIds: string[],
  from: NotificationStatus[],
  to: NotificationStatus,
  notifications: NotificationType[],
): NotificationType[] =>
  notifications.map((notification) =>
    from.includes(notification.status as NotificationStatus)
      ? {
          ...notification,
          relatedNotifications: !notification.relatedNotifications
            ? []
            : updateRelatedNotifications(
                notification.relatedNotifications,
                notificationIds,
                to,
              ),
          status: to,
        }
      : notification,
  );

export const getNotificationIdsToUpdate = (
  status: NotificationStatus,
  relatedNotifications: RelatedNotificationType[],
): string[] =>
  (relatedNotifications ?? [])
    .filter(
      (relatedNotification: RelatedNotificationType) =>
        statusToCategoryMapper[relatedNotification.status] !==
        statusToCategoryMapper[status],
    )
    .map(
      (relatedNotification: RelatedNotificationType) => relatedNotification.id,
    );

export const getRelatedNotificationsByStatus = (
  relatedNotifications: RelatedNotificationType[],
): Record<StatusType, RelatedNotificationType[]> => {
  const relatedNotificationsByStatus: Record<
    StatusType,
    RelatedNotificationType[]
  > = {
    [READ]: [],
    [UNREAD]: [],
  };

  for (const relatedNotification of relatedNotifications ?? []) {
    relatedNotificationsByStatus[
      statusToCategoryMapper[relatedNotification.status]
    ].push(relatedNotification);
  }

  return relatedNotificationsByStatus;
};

export const getNotificationText = (
  eventType: NotificationEventType,
  type: string = null,
): ReactNode => {
  const notificationText = `${eventTypeToActionMapper[eventType]?.(type) || ''}`;
  const sanitizedText = DOMPurify.sanitize(notificationText, {
    ALLOWED_TAGS: ['strong'],
  });
  return createElement('div', {
    dangerouslySetInnerHTML: {
      __html: sanitizedText,
    },
  });
};

type NotificationMergeResult = {
  notifications: NotificationType[];
  totalDelta: number;
  unreadDelta: number;
};

const dedupeNotificationsByExternalId = (
  notifications: NotificationType[],
): NotificationType[] => {
  const seen = new Set<string>();
  const dedupedNotifications: NotificationType[] = [];

  for (const notification of notifications) {
    if (seen.has(notification.notificationExternalId)) continue;
    seen.add(notification.notificationExternalId);
    dedupedNotifications.push(notification);
  }

  return dedupedNotifications;
};

const dedupeRelatedNotifications = (
  relatedNotifications: RelatedNotificationType[],
): RelatedNotificationType[] => {
  const seen = new Set<string>();
  const dedupedRelatedNotifications: RelatedNotificationType[] = [];

  for (const relatedNotification of relatedNotifications ?? []) {
    if (seen.has(relatedNotification.id)) continue;
    seen.add(relatedNotification.id);
    dedupedRelatedNotifications.push(relatedNotification);
  }

  return dedupedRelatedNotifications;
};

const getRelatedNotificationFromNotification = (
  notification: NotificationType,
): RelatedNotificationType => ({
  createdAt: notification.createdAt,
  eventType: notification.eventType,
  id: notification.notificationExternalId,
  status: notification.status,
  updatedAt: notification.updatedAt,
});

export const mergeFetchedNotifications = (
  notifications: NotificationType[],
  newNotifications: NotificationType[],
): NotificationType[] => {
  const mergedNotifications = dedupeNotificationsByExternalId(
    notifications ?? [],
  );
  const indexByExternalId = new Map<string, number>(
    mergedNotifications.map((notification, index) => [
      notification.notificationExternalId,
      index,
    ]),
  );

  for (const newNotification of newNotifications ?? []) {
    const existingIndex = indexByExternalId.get(
      newNotification.notificationExternalId,
    );

    if (existingIndex === undefined) {
      indexByExternalId.set(
        newNotification.notificationExternalId,
        mergedNotifications.length,
      );
      mergedNotifications.push(newNotification);
      continue;
    }

    const existingNotification = mergedNotifications[existingIndex];
    mergedNotifications[existingIndex] = {
      ...existingNotification,
      ...newNotification,
      relatedNotifications: dedupeRelatedNotifications(
        newNotification.relatedNotifications ??
          existingNotification.relatedNotifications ??
          [],
      ),
    };
  }

  return mergedNotifications;
};

export function getEdgeCaseNotifications(
  appSyncNotification: NotificationType,
  notifications: NotificationType[],
  pageSize: number,
): NotificationType[] {
  const notificationsToUpdate = [appSyncNotification, ...notifications];
  if (notifications.length && notifications.length % pageSize === 0) {
    notificationsToUpdate.pop();
  }
  return notificationsToUpdate;
}

export const upsertNotificationByExternalId = (
  appSyncNotification: NotificationType,
  notifications: NotificationType[],
  pageSize: number,
): NotificationMergeResult => {
  const dedupedNotifications = dedupeNotificationsByExternalId(
    notifications ?? [],
  );
  const index = dedupedNotifications.findIndex(
    (notification) =>
      notification.notificationExternalId ===
      appSyncNotification.notificationExternalId,
  );

  if (index === -1) {
    return {
      notifications: getEdgeCaseNotifications(
        appSyncNotification,
        dedupedNotifications,
        pageSize,
      ),
      totalDelta: 1,
      unreadDelta: isUnreadNotificationStatus(appSyncNotification.status)
        ? 1
        : 0,
    };
  }

  const existingNotification = dedupedNotifications[index];
  const nextNotifications = [...dedupedNotifications];
  nextNotifications[index] = {
    ...existingNotification,
    ...appSyncNotification,
    relatedNotifications: dedupeRelatedNotifications(
      appSyncNotification.relatedNotifications ??
        existingNotification.relatedNotifications ??
        [],
    ),
  };

  return {
    notifications: nextNotifications,
    totalDelta: 0,
    unreadDelta:
      Number(isUnreadNotificationStatus(appSyncNotification.status)) -
      Number(isUnreadNotificationStatus(existingNotification.status)),
  };
};

export const upsertGroupedChatNotification = (
  appSyncNotification: NotificationType,
  notifications: NotificationType[],
  pageSize: number,
): NotificationMergeResult => {
  const dedupedNotifications = dedupeNotificationsByExternalId(
    notifications ?? [],
  );
  const groupIndex = dedupedNotifications.findIndex(
    (notification) => notification.groupBy === appSyncNotification.groupBy,
  );

  if (groupIndex === -1) {
    return upsertNotificationByExternalId(
      appSyncNotification,
      dedupedNotifications,
      pageSize,
    );
  }

  const currentNotification = dedupedNotifications[groupIndex];
  const shouldPromoteCurrentNotification =
    currentNotification.notificationExternalId !==
    appSyncNotification.notificationExternalId;
  const relatedNotifications = dedupeRelatedNotifications([
    ...(shouldPromoteCurrentNotification
      ? [getRelatedNotificationFromNotification(currentNotification)]
      : []),
    ...(currentNotification.relatedNotifications ?? []),
  ]);
  const nextNotifications = [...dedupedNotifications];

  nextNotifications[groupIndex] = {
    ...currentNotification,
    ...appSyncNotification,
    relatedNotifications,
  };
  nextNotifications.sort(
    (a, b) => Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)),
  );

  return {
    notifications: nextNotifications,
    totalDelta: 0,
    unreadDelta:
      Number(isUnreadNotificationStatus(appSyncNotification.status)) -
      Number(isUnreadNotificationStatus(currentNotification.status)),
  };
};

export const getTimestamp = (timestamp: string): string =>
  timestamp.includes('T')
    ? isoToLocalDateTime(timestamp).toRelative()
    : DateTime.fromSQL(timestamp, { zone: 'utc' }).toLocal().toRelative();

export const getNotificationCounts = (
  isRead: boolean,
  eventType: NotificationEventType,
  notifications: RelatedNotificationType[],
): Record<
  | NotificationEventType.CHAT_MENTION
  | NotificationEventType.CHAT_MESSAGE
  | NotificationEventType.CHAT_REPLY,
  number
> => {
  const counts = {
    [NotificationEventType.CHAT_MENTION]:
      !isRead && eventType === NotificationEventType.CHAT_MENTION ? 1 : 0,
    [NotificationEventType.CHAT_MESSAGE]:
      !isRead && eventType === NotificationEventType.CHAT_MESSAGE ? 1 : 0,
    [NotificationEventType.CHAT_REPLY]:
      !isRead && eventType === NotificationEventType.CHAT_REPLY ? 1 : 0,
  };
  for (const { eventType: notificationEventType } of notifications ?? []) {
    counts[notificationEventType] += 1;
  }
  return counts;
};

export const isOneOnOneNotification = (groupBy: string): boolean => {
  return (
    CHANNEL_CLASSIFICATION.find((value) => groupBy.startsWith(value.url))
      ?.isOneOnOne ?? false
  );
};

export const combineSenderSupportChannelMembers = (
  data: NotificationDetailType,
): Member[] => {
  const notificationSender: SenderType = data?.sender;
  const allChannelMembers: ChatMember[] = data?.members;

  // using map to remove duplicates
  const channelMembersAndSenderMemberMap = new Map<string, Member>();

  if (notificationSender) {
    const sendingMember: Member = {
      metaData: notificationSender.metadata,
      nickname: notificationSender.nickname,
      userId: notificationSender.user_id,
    } as Member;

    channelMembersAndSenderMemberMap.set(
      notificationSender.user_id,
      sendingMember,
    );
  }

  if (allChannelMembers) {
    allChannelMembers.forEach((member) => {
      // Add only supports
      if (member.metadata?.userType === UserRoles.SUPPORT) {
        const channelMember: Member = {
          metaData: member.metadata,
          nickname: member.nickname,
          userId: member.userId,
        } as Member;

        channelMembersAndSenderMemberMap.set(
          channelMember.userId,
          channelMember,
        );
      }
    });
  }

  return Array.from(channelMembersAndSenderMemberMap.values());
};
